import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search, Plus, ArrowRight, LayoutDashboard, Users, FolderKanban, CheckSquare,
  Calendar, BarChart2, FileText, Megaphone, CalendarDays, Settings, Bot,
  Newspaper, Palette, GraduationCap, TicketCheck, History, Building2, X,
} from 'lucide-react'
import { searchApi } from '@/services/api'
import { cn } from '@/lib/utils'

const RECENT_SEARCHES_KEY = 'equipe-recent-searches'
const MAX_RECENT = 5

interface QuickAction {
  label: string
  href: string
  icon: React.ReactNode
  type: 'action'
}

interface PageItem {
  label: string
  href: string
  icon: React.ReactNode
  type: 'page'
  keywords?: string[]
}

interface SearchResultItem {
  id: string
  name?: string
  title?: string
  type: string
  href: string
}

type PaletteItem = QuickAction | PageItem | SearchResultItem

const QUICK_ACTIONS: QuickAction[] = [
  { label: 'Nuovo Cliente', href: '/clients/new', icon: <Plus className="w-4 h-4" />, type: 'action' },
  { label: 'Nuovo Progetto', href: '/projects', icon: <Plus className="w-4 h-4" />, type: 'action' },
  { label: 'I Miei Task', href: '/tasks', icon: <CheckSquare className="w-4 h-4" />, type: 'action' },
  { label: 'Calendario', href: '/calendar', icon: <Calendar className="w-4 h-4" />, type: 'action' },
  { label: 'AI Assistant', href: '/ai', icon: <Bot className="w-4 h-4" />, type: 'action' },
]

const PAGES: PageItem[] = [
  { label: 'Dashboard', href: '/', icon: <LayoutDashboard className="w-4 h-4" />, type: 'page', keywords: ['home', 'principale'] },
  { label: 'Clienti', href: '/clients', icon: <Building2 className="w-4 h-4" />, type: 'page', keywords: ['clienti', 'aziende', 'portfolio'] },
  { label: 'Progetti', href: '/projects', icon: <FolderKanban className="w-4 h-4" />, type: 'page', keywords: ['progetti', 'lavori'] },
  { label: 'Task', href: '/tasks', icon: <CheckSquare className="w-4 h-4" />, type: 'page', keywords: ['task', 'attivita', 'todo', 'kanban'] },
  { label: 'Calendario', href: '/calendar', icon: <Calendar className="w-4 h-4" />, type: 'page', keywords: ['calendario', 'agenda', 'eventi'] },
  { label: 'Report', href: '/reports', icon: <BarChart2 className="w-4 h-4" />, type: 'page', keywords: ['report', 'analytics', 'statistiche'] },
  { label: 'PED', href: '/ped', icon: <FileText className="w-4 h-4" />, type: 'page', keywords: ['piano editoriale', 'contenuti', 'social'] },
  { label: 'ADV', href: '/adv', icon: <Megaphone className="w-4 h-4" />, type: 'page', keywords: ['adv', 'advertising', 'campagne', 'pubblicita'] },
  { label: 'Eventi', href: '/events', icon: <CalendarDays className="w-4 h-4" />, type: 'page', keywords: ['eventi', 'manifestazioni'] },
  { label: 'PR', href: '/pr', icon: <Newspaper className="w-4 h-4" />, type: 'page', keywords: ['pr', 'ufficio stampa', 'comunicati'] },
  { label: 'Branding', href: '/branding', icon: <Palette className="w-4 h-4" />, type: 'page', keywords: ['branding', 'brand', 'identita visiva'] },
  { label: 'Consulenza', href: '/consulenza', icon: <GraduationCap className="w-4 h-4" />, type: 'page', keywords: ['consulenza', 'coaching', 'sessioni'] },
  { label: 'Digital Hub', href: '/digital', icon: <TicketCheck className="w-4 h-4" />, type: 'page', keywords: ['digital', 'ticketing', 'supporto'] },
  { label: 'AI Assistant', href: '/ai', icon: <Bot className="w-4 h-4" />, type: 'page', keywords: ['ai', 'intelligenza artificiale', 'chat'] },
  { label: 'Utenti', href: '/settings/users', icon: <Users className="w-4 h-4" />, type: 'page', keywords: ['utenti', 'team', 'amministrazione'] },
  { label: 'Impostazioni', href: '/settings', icon: <Settings className="w-4 h-4" />, type: 'page', keywords: ['impostazioni', 'configurazione', 'profilo'] },
]

const TYPE_LABELS: Record<string, string> = {
  clients: 'Clienti',
  projects: 'Progetti',
  tasks: 'Task',
  campaigns: 'Campagne',
  events: 'Eventi',
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  clients: <Building2 className="w-4 h-4" />,
  projects: <FolderKanban className="w-4 h-4" />,
  tasks: <CheckSquare className="w-4 h-4" />,
  campaigns: <Megaphone className="w-4 h-4" />,
  events: <CalendarDays className="w-4 h-4" />,
}

// Pill soft per tipo entità (differenziazione di categoria, tinta tenue).
const TYPE_COLORS: Record<string, string> = {
  clients: 'bg-primary-50 text-primary-600',
  projects: 'bg-purple-50 text-purple-600',
  tasks: 'bg-success-50 text-success-600',
  campaigns: 'bg-warning-50 text-warning-600',
  events: 'bg-pink-50 text-pink-600',
}

function fuzzyMatch(text: string, query: string): boolean {
  const t = text.toLowerCase()
  const q = query.toLowerCase()
  if (t.includes(q)) return true
  // Check each word starts with a query substring
  const words = t.split(/\s+/)
  return words.some((w) => w.startsWith(q))
}

function getRecentSearches(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
    return stored ? (JSON.parse(stored) as string[]) : []
  } catch {
    return []
  }
}

function saveRecentSearch(query: string) {
  try {
    const current = getRecentSearches()
    const updated = [query, ...current.filter((q) => q !== query)].slice(0, MAX_RECENT)
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
  } catch {
    // ignore
  }
}

function removeRecentSearch(query: string) {
  try {
    const current = getRecentSearches()
    const updated = current.filter((q) => q !== query)
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
  } catch {
    // ignore
  }
}

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Record<string, any[]> | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigate = useNavigate()

  // Listen for Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // Focus input and reset state when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
      setQuery('')
      setSearchResults(null)
      setSelectedIndex(0)
      setRecentSearches(getRecentSearches())
    }
  }, [open])

  // Fuzzy-matched pages by query (client-side)
  const matchedPages = query.trim().length >= 1
    ? PAGES.filter((p) => {
        const haystack = [p.label, ...(p.keywords ?? [])].join(' ')
        return fuzzyMatch(haystack, query.trim())
      })
    : []

  // Debounced API search
  const handleSearch = useCallback((value: string) => {
    setQuery(value)
    setSelectedIndex(0)

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)

    if (!value.trim() || value.trim().length < 2) {
      setSearchResults(null)
      return
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const result = await searchApi.globalSearch({ q: value.trim() })
        const data = (result as any)?.data
        setSearchResults(data?.results ?? (result as any)?.results ?? null)
      } catch {
        setSearchResults(null)
      } finally {
        setIsSearching(false)
      }
    }, 300)
  }, [])

  // Build flat list of all visible items for keyboard navigation
  const flatItems = useCallback((): Array<{ href: string; label: string }> => {
    const items: Array<{ href: string; label: string }> = []

    if (query.trim().length === 0) {
      // Default: recent searches (as shortcuts), quick actions, pages
      recentSearches.forEach((q) => items.push({ href: '', label: q }))
      QUICK_ACTIONS.forEach((a) => items.push({ href: a.href, label: a.label }))
      PAGES.forEach((p) => items.push({ href: p.href, label: p.label }))
      return items
    }

    // With query: matched pages first, then API results
    matchedPages.forEach((p) => items.push({ href: p.href, label: p.label }))

    if (searchResults) {
      Object.entries(searchResults).forEach(([type, typeItems]) => {
        if (!typeItems || typeItems.length === 0) return
        typeItems.forEach((item: any) => {
          items.push({
            href: `/${type}/${item.id}`,
            label: item.name ?? item.title ?? item.id,
          })
        })
      })
    }

    return items
  }, [searchResults, query, matchedPages, recentSearches])

  function selectItem(href: string, label?: string) {
    if (!href) {
      // Re-run search from recent
      if (label) handleSearch(label)
      return
    }
    if (label && query.trim().length >= 2) {
      saveRecentSearch(label)
    }
    setOpen(false)
    navigate(href)
  }

  // Keyboard navigation within the palette
  function handleKeyDown(e: React.KeyboardEvent) {
    const items = flatItems()
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((i) => Math.min(i + 1, items.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = items[selectedIndex]
      if (item) selectItem(item.href, item.label)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
    }
  }

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return
    const selected = listRef.current.querySelector('[data-selected="true"]')
    selected?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  if (!open) return null

  const hasQuery = query.trim().length > 0
  const hasSearchResults = searchResults !== null
  const hasAnyApiResult = hasSearchResults && Object.values(searchResults).some((items) => items && items.length > 0)
  const showMatchedPages = hasQuery && matchedPages.length > 0

  // Track global index across sections
  let globalIndex = 0

  return (
    <div
      className="fixed inset-0 z-[100]"
      onMouseDown={() => setOpen(false)}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="fixed left-1/2 top-[15%] -translate-x-1/2 w-full max-w-xl px-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-2xl shadow-modal border border-slate-200 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Cerca clienti, progetti, pagine..."
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 text-sm text-slate-800 placeholder:text-slate-400 bg-transparent outline-none"
            />
            {isSearching && (
              <div className="w-4 h-4 border-2 border-slate-200 border-t-primary-600 rounded-full animate-spin flex-shrink-0" />
            )}
            {query && !isSearching && (
              <button
                onClick={() => { setQuery(''); setSearchResults(null); setSelectedIndex(0) }}
                className="p-0.5 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <kbd className="hidden sm:flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 bg-slate-100 rounded border border-slate-200 flex-shrink-0">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[420px] overflow-y-auto py-2">
            {/* Default state: Recent searches + Quick Actions + Pages */}
            {!hasQuery && (
              <>
                {/* Recent searches */}
                {recentSearches.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                      Ricerche recenti
                    </div>
                    {recentSearches.map((recent) => {
                      const idx = globalIndex++
                      return (
                        <div
                          key={recent}
                          data-selected={selectedIndex === idx}
                          className={cn(
                            'flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition-colors group',
                            selectedIndex === idx ? 'bg-slate-100' : 'hover:bg-slate-50'
                          )}
                        >
                          <span className="flex-shrink-0 w-7 h-7 bg-slate-100 rounded-md flex items-center justify-center text-slate-400">
                            <History className="w-3.5 h-3.5" />
                          </span>
                          <button
                            onClick={() => handleSearch(recent)}
                            onMouseEnter={() => setSelectedIndex(idx)}
                            className="flex-1 text-left font-medium truncate"
                          >
                            {recent}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              removeRecentSearch(recent)
                              setRecentSearches(getRecentSearches())
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-slate-600 transition-all"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )
                    })}
                  </>
                )}

                {/* Quick Actions section */}
                <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  Azioni Rapide
                </div>
                {QUICK_ACTIONS.map((action) => {
                  const idx = globalIndex++
                  return (
                    <button
                      key={action.href + action.label}
                      data-selected={selectedIndex === idx}
                      onClick={() => selectItem(action.href)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition-colors text-left',
                        selectedIndex === idx ? 'bg-slate-100' : 'hover:bg-slate-50'
                      )}
                    >
                      <span className="flex-shrink-0 w-7 h-7 bg-slate-100 rounded-md flex items-center justify-center text-slate-500">
                        {action.icon}
                      </span>
                      <span className="flex-1 font-medium">{action.label}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                    </button>
                  )
                })}

                {/* Pages section */}
                <div className="px-3 pt-3 pb-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  Pagine
                </div>
                {PAGES.map((page) => {
                  const idx = globalIndex++
                  return (
                    <button
                      key={page.href + page.label}
                      data-selected={selectedIndex === idx}
                      onClick={() => selectItem(page.href)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 transition-colors text-left',
                        selectedIndex === idx ? 'bg-slate-100' : 'hover:bg-slate-50'
                      )}
                    >
                      <span className="flex-shrink-0 text-slate-400">{page.icon}</span>
                      <span className="flex-1">{page.label}</span>
                    </button>
                  )
                })}
              </>
            )}

            {/* Query state: fuzzy-matched pages + API results */}
            {hasQuery && (
              <>
                {/* Fuzzy-matched navigation pages */}
                {showMatchedPages && (
                  <>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                      Navigazione
                    </div>
                    {matchedPages.map((page) => {
                      const idx = globalIndex++
                      return (
                        <button
                          key={page.href + page.label}
                          data-selected={selectedIndex === idx}
                          onClick={() => selectItem(page.href)}
                          onMouseEnter={() => setSelectedIndex(idx)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition-colors text-left',
                            selectedIndex === idx ? 'bg-slate-100' : 'hover:bg-slate-50'
                          )}
                        >
                          <span className="flex-shrink-0 w-7 h-7 bg-slate-100 rounded-md flex items-center justify-center text-slate-500">
                            {page.icon}
                          </span>
                          <span className="flex-1 font-medium">{page.label}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                        </button>
                      )
                    })}
                  </>
                )}

                {/* API search results */}
                {hasAnyApiResult && (
                  Object.entries(searchResults!).map(([type, items]) => {
                    if (!items || items.length === 0) return null
                    return (
                      <div key={type}>
                        <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                          {TYPE_LABELS[type] ?? type}
                        </div>
                        {items.map((item: any) => {
                          const idx = globalIndex++
                          const href = `/${type}/${item.id}`
                          const label = item.name ?? item.title ?? item.id
                          return (
                            <button
                              key={item.id}
                              data-selected={selectedIndex === idx}
                              onClick={() => { saveRecentSearch(query.trim()); selectItem(href) }}
                              onMouseEnter={() => setSelectedIndex(idx)}
                              className={cn(
                                'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors',
                                selectedIndex === idx ? 'bg-slate-100' : 'hover:bg-slate-50'
                              )}
                            >
                              <span className={cn(
                                'flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center',
                                TYPE_COLORS[type]?.replace('text-', 'text-').replace('bg-', 'bg-') ?? 'bg-slate-100 text-slate-500'
                              )}>
                                {TYPE_ICONS[type] ?? <Search className="w-4 h-4" />}
                              </span>
                              <span className="flex-1 text-slate-700 truncate font-medium">{label}</span>
                              <span className={cn(
                                'flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded',
                                TYPE_COLORS[type] ?? 'bg-slate-100 text-slate-600'
                              )}>
                                {TYPE_LABELS[type] ?? type}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    )
                  })
                )}

                {/* No results at all */}
                {!isSearching && !showMatchedPages && (!hasSearchResults || !hasAnyApiResult) && (
                  <div className="py-10 text-center text-sm text-slate-400">
                    Nessun risultato per &ldquo;{query}&rdquo;
                  </div>
                )}

                {/* Searching spinner */}
                {isSearching && !showMatchedPages && (
                  <div className="py-8 flex items-center justify-center gap-2 text-sm text-slate-400">
                    <div className="w-4 h-4 border-2 border-slate-200 border-t-primary-600 rounded-full animate-spin" />
                    Ricerca in corso...
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer hint */}
          <div className="px-4 py-2 border-t border-slate-200 flex items-center gap-4 text-[10px] text-slate-400">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-slate-100 rounded border border-slate-200">↑↓</kbd>
              naviga
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-slate-100 rounded border border-slate-200">↵</kbd>
              apri
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-slate-100 rounded border border-slate-200">ESC</kbd>
              chiudi
            </span>
            <span className="ml-auto flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-100 rounded border border-slate-200">⌘K</kbd>
              apri/chiudi
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
